import { IntlShape } from 'react-intl'

import { BillingColumns } from '../../details-container/BillingColumns'
import {
  BookingTotalPriceObjectFragment,
  DetailsBillingSectionClientPricingItemFragment,
  PriceObjectOutputDto,
  ProposalDetailsSummaryClientPricingItemFragment,
  ServiceProviderCategoryEnum,
} from '../../../../../../lib/graphql'
import { IconProps } from '../../../../../atoms/icons/icon'
import { UserTypeContextEnum } from '../../../../../contexts'

export type DetailsBillingSection = {
  clientPricingItems: DetailsBillingSectionClientPricingItemFragment[]
  categories: ServiceProviderCategoryEnum[]
  label: string
  icon: IconProps['name']
}

export const createDetailsBillingSections = (
  intl: IntlShape,
): Omit<DetailsBillingSection, 'clientPricingItems'>[] => [
  {
    categories: [ServiceProviderCategoryEnum.BATCH_PROVISIONS],
    label: intl.formatMessage({
      id: 'ui.getDetailsBillingSectionProps.batchProvisionsLabel',
      defaultMessage: 'Forfait',
    }),
    icon: 'book',
  },
  {
    categories: [
      ServiceProviderCategoryEnum.HOUSE,
      ServiceProviderCategoryEnum.TAXES,
    ],
    label: intl.formatMessage({
      id: 'ui.getDetailsBillingSectionProps.houseLabel',
      defaultMessage: 'Logement',
    }),
    icon: 'home-filled',
  },
  {
    categories: [ServiceProviderCategoryEnum.RESTAURATION],
    label: intl.formatMessage({
      id: 'ui.getDetailsBillingSectionProps.restorationLabel',
      defaultMessage: 'Restauration',
    }),
    icon: 'restaurant',
  },
  {
    categories: [ServiceProviderCategoryEnum.ACTIVITY],
    label: intl.formatMessage({
      id: 'ui.getDetailsBillingSectionProps.activitiesLabel',
      defaultMessage: 'Activités',
    }),
    icon: 'challenge-sport',
  },
  {
    categories: [ServiceProviderCategoryEnum.TRANSPORT],
    label: intl.formatMessage({
      id: 'ui.getDetailsBillingSectionProps.transportLabel',
      defaultMessage: 'Transport',
    }),
    icon: 'train',
  },
]

export const getDetailsBillingSectionProps = ({
  clientPricingItems,
  intl,
}: {
  clientPricingItems: ProposalDetailsSummaryClientPricingItemFragment[]
  intl: IntlShape
}): DetailsBillingSection[] => {
  const sections = createDetailsBillingSections(intl)

  return sections
    .map((section) => ({
      ...section,
      clientPricingItems:
        clientPricingItems.filter((priceItem) =>
          section.categories.includes(priceItem.category),
        ) ?? [],
    }))
    .filter((section) => section.clientPricingItems.length > 0)
}

export const ProposalDetailsTotalColumns = [
  BillingColumns.description,
  BillingColumns.quantity,
  BillingColumns.unitHT,
  BillingColumns.totalHT,
  BillingColumns.totalTTC,
]

export const getProposalDetailsSummaryColumns = ({
  userType,
}: {
  userType: UserTypeContextEnum
}) => {
  const ProposalDetailsColumnsUser = [
    BillingColumns.unit,
    ...ProposalDetailsTotalColumns,
  ]

  const ProposalDetailsColumnsOwner = [
    BillingColumns.ownerFeesRate,
    ...ProposalDetailsColumnsUser,
  ]

  let columns: BillingColumns[] = []
  if (userType === UserTypeContextEnum.OWNER) {
    columns = ProposalDetailsColumnsOwner
  } else {
    columns = ProposalDetailsColumnsUser
  }
  return columns
}

export const getTotalPrice = ({
  price,
  userType,
}: {
  price: BookingTotalPriceObjectFragment | undefined
  userType: UserTypeContextEnum
}): PriceObjectOutputDto | undefined => {
  if (!price) {
    return undefined
  }

  return {
    basePrice:
      price[
        userType === UserTypeContextEnum.USER
          ? 'priceWithUserFees'
          : 'priceWithOwnerFees'
      ],
    priceWithDiscount:
      price[
        userType === UserTypeContextEnum.USER
          ? 'priceWithDiscountAndFees'
          : 'priceWithDiscountAndOwnerFees'
      ],
    fees: price.fees,
    feesWithDiscount: price.feesWithDiscount,
  }
}
